import React, { useEffect, useState, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import PublicRouter from "./public-router";
import { publicRoute } from "./routes";
import { LoaderComponent } from "../shared/components/loaders/page-loader";
import { DrawerHeader } from "../shared/components/navigations/side-nav";
import { useTransactionCertificateStore } from "../store/transaction-certificates.store";
import { TCAppConfig } from "../shared/app-configs/tc-app-configs";
import { useApi } from "../shared/hooks/useApi";
import _ from 'lodash';
import { Typography } from "@mui/material";
import { isAdminCBID, isAdmin } from "../shared/utils/admin-utils";
import ProtectedAdminRoute from "../shared/components/protected-routes/protected-admin-route";

const AuthRoute: React.FC = () => {
  const { cbLicensingCode, getUserInfo, setUserInfo, setCbLicensingCode, setOriginalAdminCBID, originalAdminCBID } = useTransactionCertificateStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [isNoEmail,setNoEmail] =  useState<boolean>(false);
  const { executeGet } = useApi();

  useEffect(() => {    
    // Fetch CB Licensing Code if it is not already set
    const fetchCBLicensingCode = async () => {
      try {
        // Assume getUserInfo fetches or returns the current user info
        const userInfo = await getUserInfo;
        
        if (!userInfo?.userEmail && !_.isEmpty(userInfo?.displayName)) {
          // User has no email
          setLoading(false);
          setNoEmail(true);
          return;
        }

        if (userInfo && userInfo?.userEmail) {
          if (originalAdminCBID) {
            const adminStatus = isAdminCBID(originalAdminCBID);
            
            // For admins on page reload: if cbLicensingCode differs from originalAdminCBID,
            // it means they were viewing another user's data - reset to their admin CB-ID
            if (adminStatus && cbLicensingCode !== originalAdminCBID) {
              setCbLicensingCode(originalAdminCBID);
            }
            
            // If cbLicensingCode is not set at all, set it to originalAdminCBID
            if (!cbLicensingCode) {
              setCbLicensingCode(originalAdminCBID);
            }
            setLoading(false);
            return;
          }

          // First time login - fetch user's CB-ID
          const response: any = await executeGet(TCAppConfig.GET_CB_CO_CODE, {
            email: userInfo?.userEmail,
          });
          
          if (response?.status === 200 && response?.data?.length > 0) {
            const updatedUserInfo = { ...userInfo };
            setUserInfo(updatedUserInfo);
            
            // Set the user's CB-ID (assuming the first element's ID)
            const userCBId = response?.data[0]?.ID;
            
            // Check if this CB-ID is an admin CB-ID
            const adminStatus = isAdminCBID(userCBId);
            
            // Set the CB Licensing Code
            setCbLicensingCode(userCBId);
            
            // If user is admin, store their original admin CB-ID
            if (adminStatus) {
              setOriginalAdminCBID(userCBId);
            } else {
              console.error(`[AuthRoute]  User is NOT admin, originalAdminCBID will remain empty`);
            }
            
            setLoading(false);
          } else {
            // No CB-ID found
            setLoading(false);
          }
        }
      } catch (error) {
        setLoading(false);
      }
    };

    fetchCBLicensingCode();
  }, []);

  if (loading || !cbLicensingCode) {
    // Show loader while fetching data
    return <LoaderComponent />;
  }else if(isNoEmail){
    return(
      <LoaderComponent error = {true} errorMsg="Since you donot have a valid email, you donot have access to the application"/>
    )
  }
   
  // Check admin status using both methods for reliability
  const userIsAdmin = isAdmin() || (originalAdminCBID && isAdminCBID(originalAdminCBID));


  return (
    <Suspense fallback={<><DrawerHeader /><LoaderComponent /></>}>
      <Routes>
        {/* If user is admin, redirect to admin dashboard, otherwise to home */}
        <Route path="/" element={<Navigate to={userIsAdmin ? "/admin" : "/home"} replace />} />
        <Route element={<PublicRouter />}>
          {publicRoute?.map(({ path, id, component: Component, requiresAdmin }) => (
            <Route 
              key={id} 
              path={path} 
              element={
                requiresAdmin ? (
                  <ProtectedAdminRoute>
                    <Component />
                  </ProtectedAdminRoute>
                ) : (
                  <Component />
                )
              }
            />
          ))}
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AuthRoute;